import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';

const ProjectsContext = createContext();

export const useProjects = () => useContext(ProjectsContext);

export const ProjectsProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  // Fetch all projects from Supabase
  const fetchProjects = async () => {
    if (!currentUser) {
      setProjects([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform database format to app format
      const transformedProjects = data.map(p => ({
        id: p.id,
        location: p.location || { country: '', city: '' },
        stpType: p.stp_type,
        description: p.description,
        driver: p.driver,
        recommendation: p.recommendation,
        preSpend: p.pre_spend,
        nextSteps: p.next_steps,
        modelDate: p.model_date || { from: '', to: '' },
        decision: p.decision,
        goa: p.goa || { leaseLiability: '', capexLiability: '' },
        pnlImpact: p.pnl_impact,
        rag: p.rag || {},
        riskItems: p.risk_items || {},
        financialSummary: p.financial_summary || [],
        status: p.status || 'draft',
        approvalData: p.approval_data || null,
        createdAt: p.created_at,
        updatedAt: p.updated_at,
        userId: p.user_id
      }));

      setProjects(transformedProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [currentUser]);

  const saveProject = async (project) => {
    if (!currentUser) return;

    try {
      const { data, error } = await supabase
        .from('projects')
        .insert({
          user_id: currentUser.id,
          location: project.location,
          stp_type: project.stpType,
          description: project.description,
          driver: project.driver,
          recommendation: project.recommendation,
          pre_spend: project.preSpend,
          next_steps: project.nextSteps,
          model_date: project.modelDate,
          decision: project.decision,
          goa: project.goa,
          pnl_impact: project.pnlImpact,
          rag: project.rag,
          risk_items: project.riskItems,
          financial_summary: project.financialSummary
        })
        .select()
        .single();

      if (error) throw error;

      // Refresh projects list
      await fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error saving project: ' + error.message);
    }
  };

  const updateProject = async (id, updates) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update({
          location: updates.location,
          stp_type: updates.stpType,
          description: updates.description,
          driver: updates.driver,
          recommendation: updates.recommendation,
          pre_spend: updates.preSpend,
          next_steps: updates.nextSteps,
          model_date: updates.modelDate,
          decision: updates.decision,
          goa: updates.goa,
          pnl_impact: updates.pnlImpact,
          rag: updates.rag,
          risk_items: updates.riskItems,
          financial_summary: updates.financialSummary,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Refresh projects list
      await fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Error updating project: ' + error.message);
    }
  };

  const deleteProject = async (id) => {
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh projects list
      await fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Error deleting project: ' + error.message);
    }
  };

  return (
    <ProjectsContext.Provider value={{ projects, loading, saveProject, updateProject, deleteProject, refreshProjects: fetchProjects }}>
      {children}
    </ProjectsContext.Provider>
  );
};
